import { Languages } from '@/constants'
import { useNotification, useStore } from '@/containers'
import type { Product } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import Link from 'next/link'
import type { ChangeEvent, Dispatch, SetStateAction } from 'react'
import { useMemo } from 'react'
import ImageOptimize from '../ImageOptimize'
export interface BundledProductCardProps {
  item: Product
  addToListBundle: string[]
  setAddToListBundle: Dispatch<SetStateAction<string[]>>
}
const BundledProductCard = ({
  item,
  addToListBundle,
  setAddToListBundle,
}: BundledProductCardProps) => {
  const productName = useMemo(() => {
    return getTranslate(item?.name)
  }, [item])

  const { selectedLang } = useStore()
  const { systemConfig } = useNotification()
  const currency = (lang: string) => {
    switch (lang) {
      case 'vi':
        return 'VND'
      case 'en':
        return 'VND'
      default:
        return 'VND'
    }
  }
  const handleAddListBundle = (event: ChangeEvent<HTMLInputElement>): void => {
    const checkbox = event.target as HTMLInputElement
    const value: string = checkbox.value

    setAddToListBundle(prevList => {
      const newList = [...prevList]
      if (checkbox.checked) {
        if (!newList.includes(value)) {
          newList.push(value)
        }
      } else {
        const index = newList.indexOf(value)
        if (index !== -1) {
          newList.splice(index, 1)
        }
      }
      return newList
    })
  }
  return (
    <div className='flex h-full gap-3 overflow-hidden rounded-lg bg-white-400 px-2 pb-4 pt-4 w-full form-check-default small middle box-checkbox'>
      <input
        className='form-checkbox-input'
        id={'id_' + item?._id}
        type='checkbox'
        name={'bundle_' + item?._id}
        value={item?._id}
        defaultChecked={addToListBundle.includes(item?._id)}
        onChange={e => handleAddListBundle(e)}
      />
      <label
        className='form-checkbox-label flex w-full gap-3 pl-2'
        htmlFor={'id_' + item?._id}
      >
        <div className='image-thumb relative block w-full max-w-[70px] flex-[0_0_70px] overflow-hidden rounded-t-sm'>
          <Link
            href={'/' + getTranslate(item?.slugs?.value) + item?.slugs?.postfix}
            className={`relative block w-full overflow-hidden pt-[100%] focus:border-0 focus:outline-0`}
          >
            <ImageOptimize
              className={`absolute left-0 top-0 aspect-square h-full w-full object-cover`}
              src={
                item?.thumbnail
                  ? item?.thumbnail
                  : systemConfig?.imagePlaceholder ||
                    process.env.NEXT_PUBLIC_IMAGE_PLACEHOLDER ||
                    ''
              }
              alt={productName}
              width={70}
              height={70}
              loading={'lazy'}
              thumbnail
            />
          </Link>
        </div>
        <div className='flex flex-wrap flex-col gap-0.5'>
          <Link
            href={'/' + getTranslate(item?.slugs?.value) + item?.slugs?.postfix}
            className='text-gray-800 text-xs font-semibold leading-4'
          >
            {productName}
          </Link>
          <div className='originPrice flex-start flex items-center gap-x-1 text-black text-xs font-normal line-through leading-5'>
            {item?.price &&
              item?.price
                .toLocaleString(Languages.VN, {
                  style: 'currency',
                  currency: currency(selectedLang as string),
                })
                .replace(/\s₫/g, 'đ')}
          </div>
          <div className='sale-price flex items-baseline gap-3'>
            <Link
              href={
                '/' + getTranslate(item?.slugs?.value) + item?.slugs?.postfix
              }
              className='flex items-center gap-x-1 text-purple text-base leading-none font-bold'
            >
              {item?.salePrice &&
                item?.salePrice
                  .toLocaleString(Languages.VN, {
                    style: 'currency',
                    currency: currency(selectedLang as string),
                  })
                  .replace(/\s₫/g, 'đ')}
            </Link>
            {Math.ceil(100 - (item?.salePrice / item?.price) * 100) > 0 && (
              <span className='discount flex items-baseline gap-[2px]'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='10'
                  height='10'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M14.0004 6C13.7352 6 13.4808 6.10536 13.2933 6.29289C13.1058 6.48043 13.0004 6.73478 13.0004 7V11.586L2.70741 1.293C2.5188 1.11084 2.2662 1.01005 2.00401 1.01233C1.74181 1.0146 1.491 1.11977 1.30559 1.30518C1.12018 1.49059 1.01501 1.7414 1.01273 2.0036C1.01045 2.2658 1.11125 2.5184 1.29341 2.707L11.5864 13H7.00041C6.73519 13 6.48084 13.1054 6.2933 13.2929C6.10576 13.4804 6.00041 13.7348 6.00041 14C6.00041 14.2652 6.10576 14.5196 6.2933 14.7071C6.48084 14.8946 6.73519 15 7.00041 15H14.0004C14.2656 15 14.52 14.8946 14.7075 14.7071C14.8951 14.5196 15.0004 14.2652 15.0004 14V7C15.0004 6.73478 14.8951 6.48043 14.7075 6.29289C14.52 6.10536 14.2656 6 14.0004 6Z'
                    fill='#FF424E'
                  />
                </svg>
                <span className='flex items-center text-percent text-primary-second'>
                  <span className='value'>
                    {Math.ceil(100 - (item?.salePrice / item?.price) * 100)}
                  </span>
                  <span className='unit sm:text-[10px] md:text-xs'>%</span>
                </span>
              </span>
            )}
          </div>
        </div>
      </label>
    </div>
  )
}

export default BundledProductCard
